// Direction screen — clock-in / clock-out choice. Reuses the mockup
// KioskWelcome two-big-buttons grammar (primary emerald + raised white)
// for the in/out pair; while the punch is in flight both targets are
// disabled and the submitting line replaces the lead.

import { ArrowLeft, LoaderCircle, LogIn, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { PunchDirection } from "@/api/punch";

export interface DirectionScreenProps {
  /** Direction currently in flight; null while the user is still choosing. */
  readonly submittingDirection: PunchDirection | null;
  readonly onPunch: (direction: PunchDirection) => void;
  readonly onBack: () => void;
}

// min(340px,100%) keeps the tablet minimum but lets a 375px portrait viewport
// win: a partially off-screen in/out button risks punching the wrong
// direction, so the pair wraps into a full-width stack instead of clipping.
const CHOICE_BASE =
  "flex min-w-[min(340px,100%)] max-w-full cursor-pointer flex-col items-center gap-2 rounded-[26px] px-8 py-4 transition-colors sm:gap-4 sm:px-[56px] sm:py-[40px] " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] " +
  "disabled:cursor-not-allowed disabled:opacity-60";

export function DirectionScreen({
  submittingDirection,
  onPunch,
  onBack,
}: DirectionScreenProps): React.ReactElement {
  const { t } = useTranslation();
  const submitting = submittingDirection !== null;

  return (
    <div className="flex flex-1 flex-col px-6 pb-9 sm:px-16">
      <div className="flex flex-1 flex-col items-center justify-center">
        <h1 className="m-0 text-center font-serif text-[34px] font-normal tracking-[-0.015em] text-[var(--fg-primary)] sm:text-[48px]">
          {t("kiosk.direction.title")} <em className="italic">{t("kiosk.direction.title_em")}</em>
        </h1>
        <p
          role="status"
          className="mt-3 text-center font-serif text-[17px] italic text-[var(--fg-muted)] sm:mt-4 sm:text-[20px]"
        >
          {submitting ? t("kiosk.direction.submitting") : t("kiosk.direction.lead")}
        </p>

        <div className="mt-6 flex w-full flex-wrap justify-center gap-4 sm:mt-12 sm:gap-[22px]">
          <button
            type="button"
            disabled={submitting}
            onClick={() => onPunch("in")}
            className={`${CHOICE_BASE} border-0 bg-[var(--accent)] text-[var(--fg-on-accent)] shadow-[var(--shadow-md)] active:bg-[var(--accent-active)]`}
          >
            {submittingDirection === "in" ? (
              <LoaderCircle size={52} strokeWidth={1.6} aria-hidden="true" className="h-9 w-9 animate-spin sm:h-[52px] sm:w-[52px]" />
            ) : (
              <LogIn size={52} strokeWidth={1.6} aria-hidden="true" className="h-9 w-9 sm:h-[52px] sm:w-[52px]" />
            )}
            <span className="font-sans text-[28px] font-semibold tracking-[-0.02em]">
              {t("kiosk.direction.in")}
            </span>
            <span className="font-serif text-[16px] italic">
              {t("kiosk.direction.in_sub")}
            </span>
          </button>

          <button
            type="button"
            disabled={submitting}
            onClick={() => onPunch("out")}
            className={`${CHOICE_BASE} border border-[var(--border-default)] bg-[var(--bg-raised)] text-[var(--fg-primary)] shadow-[var(--shadow-sm)] active:bg-[var(--bg-subtle)]`}
          >
            {submittingDirection === "out" ? (
              <LoaderCircle
                size={52}
                strokeWidth={1.6}
                aria-hidden="true"
                className="h-9 w-9 animate-spin text-[var(--accent)] sm:h-[52px] sm:w-[52px]"
              />
            ) : (
              <LogOut
                size={52}
                strokeWidth={1.6}
                aria-hidden="true"
                className="h-9 w-9 text-[var(--accent)] sm:h-[52px] sm:w-[52px]"
              />
            )}
            <span className="font-sans text-[28px] font-semibold tracking-[-0.02em]">
              {t("kiosk.direction.out")}
            </span>
            <span className="font-serif text-[16px] italic text-[var(--fg-muted)]">
              {t("kiosk.direction.out_sub")}
            </span>
          </button>
        </div>
      </div>

      <div className="flex items-center border-t border-[var(--border-subtle)] pt-4">
        <button
          type="button"
          disabled={submitting}
          onClick={onBack}
          className="flex cursor-pointer items-center gap-2 rounded-[14px] border border-[var(--border-default)] bg-[var(--bg-raised)] px-9 py-[18px] font-sans text-[18px] font-medium text-[var(--fg-secondary)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] active:bg-[var(--bg-subtle)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <ArrowLeft size={20} aria-hidden="true" className="text-[var(--fg-muted)]" />
          {t("kiosk.direction.back")}
        </button>
      </div>
    </div>
  );
}
